import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Box } from 'atoms/icons/Box';
import { Cart } from 'atoms/icons/Cart';
import { Gavel } from 'atoms/icons/Gavel';
import { Swap } from 'atoms/icons/Swap';
import { BaseAccordion } from 'atoms/layout/Accordion';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyL, BodyM, BrandM, BrandXXL } from 'atoms/typography';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { laptopAndAbove } from 'style/mediaQuery';

import av1 from './assets/SORARE-101VIDEO.av1.webm';

const Root = styled.section`
  display: flex;
  padding: var(--quadruple-unit);
  align-items: flex-start;
  justify-content: space-around;
  @media ${laptopAndAbove} {
    padding: calc(var(--unit) * 15) calc(var(--unit) * 10);
    gap: var(--quadruple-unit);
    height: 100vh;
  }
`;

const TextContainer = styled(Vertical).attrs({
  gap: 6,
})`
  justify-content: flex-start;
  min-height: calc(var(--unit) * 93);
  @media ${laptopAndAbove} {
    width: 50%;
  }
`;

const VideoContainer = styled(Horizontal).attrs({
  center: true,
})`
  @media ${laptopAndAbove} {
    width: 50%;
    min-height: calc(var(--unit) * 93);
  }
`;

const AccordionTitle = styled(BrandM)`
  color: var(--c-nd-400-opaque);
  &.isOpen {
    color: var(--white);
  }
`;

export const ScoutAndCollect = () => {
  const isLaptop = useIsLaptop();
  const [openAccordionId, setOpenAccordionId] = useState<
    | 'win-auctions'
    | 'buy-instantly'
    | 'trade-directly'
    | 'earn-rewards'
    | undefined
  >('win-auctions');

  const accordionVideos = {
    'win-auctions': av1,
    'buy-instantly': av1,
    'trade-directly': av1,
    'earn-rewards': av1,
  };
  const [displayedVideo, setDisplayedVideo] = useState(
    accordionVideos['win-auctions']
  );

  const handleAccordionChange = (
    accordionId:
      | 'win-auctions'
      | 'buy-instantly'
      | 'trade-directly'
      | 'earn-rewards'
  ) => {
    if (openAccordionId === accordionId) {
      setOpenAccordionId(undefined);
    } else {
      setDisplayedVideo(accordionVideos[accordionId]);
      setOpenAccordionId(accordionId);
    }
  };

  return (
    <Root id="HowToPlay.navbar.scout-and-collect">
      <TextContainer>
        <Vertical gap={4}>
          <Vertical gap={2}>
            <BrandM uppercase color="var(--c-limited)">
              <FormattedMessage
                id="HowToPlay.ScoutAndCollect.howToSignPlayers"
                defaultMessage="How to sign players"
              />
            </BrandM>
            <BrandXXL uppercase>
              <FormattedMessage
                id="HowToPlay.ScoutAndCollect.scoutAndCollect"
                defaultMessage="Scout and Collect"
              />
            </BrandXXL>
          </Vertical>
          <BodyL>
            <FormattedMessage
              id="HowToPlay.ScoutAndCollect.scoutAndCollectDescription1"
              defaultMessage="Build your team using officially licensed digital player cards from the NBA, MLB, the Premier League, Bundesliga, LALIGA EA Sports, Ligue 1, and more!"
            />
          </BodyL>
          <BodyL>
            <FormattedMessage
              id="HowToPlay.ScoutAndCollect.scoutAndCollectDescription2"
              defaultMessage="There are several ways to collect player cards, including:"
            />
          </BodyL>
        </Vertical>
        <BaseAccordion
          expanded={openAccordionId === 'win-auctions'}
          onExpandChange={() => handleAccordionChange('win-auctions')}
          title={
            <Horizontal>
              <Gavel />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId === 'win-auctions',
                })}
              >
                <FormattedMessage
                  id="HowToPlay.ScoutAndCollect.winAuctions"
                  defaultMessage="Win Auctions"
                />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <Vertical gap={2}>
            <BodyM>
              <FormattedMessage
                id="HowToPlay.ScoutAndCollect.bidOnNewlyReleasedCards"
                defaultMessage="Bid on newly released cards from Sorare."
              />
            </BodyM>
            {!isLaptop && (
              <video width="100%" height="100%" muted autoPlay loop>
                <source src={accordionVideos['win-auctions']} />
              </video>
            )}
          </Vertical>
        </BaseAccordion>
        <BaseAccordion
          expanded={openAccordionId === 'buy-instantly'}
          onExpandChange={() => handleAccordionChange('buy-instantly')}
          title={
            <Horizontal>
              <Cart />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId === 'buy-instantly',
                })}
              >
                <FormattedMessage
                  id="HowToPlay.ScoutAndCollect.buyInstantly"
                  defaultMessage="Buy Instantly"
                />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <Vertical gap={2}>
            <BodyM>
              <FormattedMessage
                id="HowToPlay.ScoutAndCollect.bidOnNewlyReleasedCards"
                defaultMessage="Bid on newly released cards from Sorare."
              />
            </BodyM>
            {!isLaptop && (
              <video width="100%" height="100%" muted autoPlay loop>
                <source src={accordionVideos['buy-instantly']} />
              </video>
            )}
          </Vertical>
        </BaseAccordion>
        <BaseAccordion
          expanded={openAccordionId === 'trade-directly'}
          onExpandChange={() => handleAccordionChange('trade-directly')}
          title={
            <Horizontal>
              <Swap color="#927AFF" />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId === 'trade-directly',
                })}
              >
                <FormattedMessage
                  id="HowToPlay.ScoutAndCollect.tradeDirectly"
                  defaultMessage="Trade Directly"
                />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <Vertical gap={2}>
            <BodyM>
              <FormattedMessage
                id="HowToPlay.ScoutAndCollect.bidOnNewlyReleasedCards"
                defaultMessage="Bid on newly released cards from Sorare."
              />
            </BodyM>
            {!isLaptop && (
              <video width="100%" height="100%" muted autoPlay loop>
                <source src={accordionVideos['trade-directly']} />
              </video>
            )}
          </Vertical>
        </BaseAccordion>
        <BaseAccordion
          expanded={openAccordionId === 'earn-rewards'}
          onExpandChange={() => handleAccordionChange('earn-rewards')}
          title={
            <Horizontal>
              <Box color="#927AFF" />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId === 'earn-rewards',
                })}
              >
                <FormattedMessage
                  id="HowToPlay.ScoutAndCollect.earnRewards"
                  defaultMessage="Earn Rewards"
                />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <Vertical gap={2}>
            <BodyM>
              <FormattedMessage
                id="HowToPlay.ScoutAndCollect.bidOnNewlyReleasedCards"
                defaultMessage="Bid on newly released cards from Sorare."
              />
            </BodyM>
            {!isLaptop && (
              <video width="100%" height="100%" muted autoPlay loop>
                <source src={accordionVideos['earn-rewards']} />
              </video>
            )}
          </Vertical>
        </BaseAccordion>
      </TextContainer>
      {!!isLaptop && (
        <VideoContainer>
          <video
            key={displayedVideo}
            width="100%"
            height="100%"
            muted
            autoPlay
            loop
          >
            <source src={displayedVideo} />
          </video>
        </VideoContainer>
      )}
    </Root>
  );
};
